import React, { useContext } from "react";
// @ts-ignore
import * as d3 from "d3";

import "./BpsStyles.css";
import Images from "../../assets/images/Images";
import Colors from "../../assets/colors/Colors";

import styled from "styled-components";
import BackComponentWrapper from "../RankingsAndLevelsComponent/Components/BackComponentWrapper";
import Constants from "../../data/Constants";
import LineChart from "../d3/Bps/LineChart";
import { GlobalDataContext } from "../../contexts/GlobalDataContext";
import { useTranslation } from "react-i18next";

interface Props {
  bkgColor: string;
  bkgImg: string;
  children?: React.ReactNode;
}

const BpsContainerDiv = styled.div`
  display: flex;
  height: 100%;
  flex: 1 1 auto;
  flex-direction: column;
  position: relative;
  z-index: 0;

  &:after {
    content: "";
    background-attachment: fixed;
    background-image: url(${(props: Props) => props.bkgImg});
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 0.1;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
  }

  &:before {
    content: "";
    background-color: ${(props: Props) => props.bkgColor};
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -2;
  }
`;

const BpsMarketComponent: React.FC = () => {
  return (
    <BpsContainerDiv
      bkgColor={Colors.backgroundGray}
      bkgImg={Images.mapBackgroundLife}
    >
      <BackComponentWrapper backRoute={Constants.routes.bps} />
      <BpsMarketContent />
    </BpsContainerDiv>
  );
};

const StyledGraphicsLegend = styled.div<{ children?: React.ReactNode }>`
  flex: 1;
  display: flex;
  justify-content: flex-start;

  & .color-label {
    width: 17px;
    height: 17px;
    margin: auto 10px;
  }

  & .color-label-container-short {
    flex: 0;
    display: flex;
    justify-content: flex-end;
  }

  & .color-label-container-long {
    flex: 1;
    display: flex;
    justify-content: flex-end;
  }

  & .text-label {
    font-family: Mapfre-Bold;
    font-size: 16px;
    margin: auto 0;
    flex: 1;
  }
`;

const Charts = styled.div<{ children?: React.ReactNode }>`
  color: white;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;

  .bps-charts__legend {
    display: flex;
    flex: 0 0 auto;
    flex-direction: row;
    justify-content: space-around;
    margin-bottom: 40px;
    margin-left: 60px;
    margin-right: 60px;
  }

  .bps-charts__columns-wrapper {
    display: flex;
    flex: 1 0 auto;
    flex-direction: row;
    margin: 0 50px;
  }

  .bps-charts__column {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    padding: 0 40px;
  }

  .bps-charts__chart-title {
    display: flex;
    flex-direction: row;
    flex: 0 0 auto;
  }

  .bps-charts__chart {
    flex: 1 0 auto;
  }

  .bps-chart-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    font-size: 1.3rem;

    & .bps-chart-header__name {
      font-family: Mapfre-Bold;
    }

    & .bps-chart-header__description {
      font-family: Mapfre-Light;
    }

    & .bps-chart-header__unit {
      display: block;
      font-family: Mapfre-Light;
      font-size: 0.85em;
      margin-top: 5px;
    }

    & .bps-chart-header__branch {
      display: flex;
      justify-content: flex-end;
      margin-left: 25px;
      text-transform: uppercase;
    }
  }

  .bps-chart {
    flex: 1 0 auto;
    position: relative;
    margin-bottom: 50px;

    & svg {
      position: absolute;
      height: 100%;
      width: 100%;
    }
  }
`;

const colors = {
  DEVELOPED: "#A44FD1",
  BRICS: "#4E8F7E",
  G7: "#1E6BD1",
  RAISING: "#DA7F52",
};

const BpsMarketContent: React.FC = () => {
  // @ts-ignore
  const { bpsData } = useContext(GlobalDataContext);

  const bpsDataLife = bpsData
    .filter((a: any) => a.is_life == true)
    .map((r: any) => ({
      year: r.year,
      bps: parseFloat(r.bps_proportion),
      region: r.region,
    }));

  const bpsDataNoLife = bpsData
    .filter((a: any) => a.is_life == false)
    .map((r: any) => ({
      year: r.year,
      bps: parseFloat(r.bps_proportion),
      region: r.region,
    }));

  // @ts-ignore
  const maxLife = parseFloat(
    d3.extent(bpsDataLife.map((record: any) => record.bps))[1] ?? "0"
  );
  // @ts-ignore
  const maxNoLife = parseFloat(
    d3.extent(bpsDataNoLife.map((record: any) => record.bps))[1] ?? "0"
  );

  const yDomain = d3.scaleLinear().domain([0, Math.max(maxLife, maxNoLife)]);

  const { t } = useTranslation();

  return (
    <Charts className="bps-charts">
      <div className="bps-charts__legend">
        <StyledGraphicsLegend>
          <div className={"color-label-container-short"}>
            <div
              className="color-label"
              style={{ backgroundColor: colors.RAISING }}
            />
          </div>
          <div className="text-label">{t("bpsMarket.legend.1")}</div>
        </StyledGraphicsLegend>
        <StyledGraphicsLegend>
          <div className={"color-label-container-long"}>
            <div
              className="color-label"
              style={{ backgroundColor: colors.BRICS }}
            />
          </div>
          <div className="text-label">{t("bpsMarket.legend.2")}</div>
        </StyledGraphicsLegend>
        <StyledGraphicsLegend>
          <div className={"color-label-container-short"}>
            <div
              className="color-label"
              style={{ backgroundColor: colors.DEVELOPED }}
            />
          </div>
          <div className="text-label">{t("bpsMarket.legend.3")}</div>
        </StyledGraphicsLegend>
        <StyledGraphicsLegend>
          <div className={"color-label-container-long"}>
            <div
              className="color-label"
              style={{ backgroundColor: colors.G7 }}
            />
          </div>
          <div className="text-label">{t("bpsMarket.legend.4")}</div>
        </StyledGraphicsLegend>
      </div>
      <div className="bps-charts__columns-wrapper">
        <div className="bps-charts__column">
          <div className="bps-chart-header">
            <div className="bps-chart-header__title">
              <span className="bps-chart-header__name">
                {t("bpsMarket.proportionTitle")}
              </span>
              <span className="bps-chart-header__description">
                {t("bpsMarket.proportionDescription")}
              </span>
              <span className="bps-chart-header__unit">
                {t("bpsMarket.proportionUnit")}
              </span>
            </div>
            <div className="bps-chart-header__branch">
              {t("lifeSelector.life")}
            </div>
          </div>
          <div className="bps-chart">
            <LineChart
              bpsRecords={bpsDataLife}
              chartWidth={400}
              chartHeight={400}
              selectedSeries={undefined}
              yDomain={yDomain}
            />
          </div>
        </div>

        <div className="bps-charts__column">
          <div className="bps-chart-header">
            <div className="bps-chart-header__title">
              <span className="bps-chart-header__name">
                {t("bpsMarket.proportionTitle")}
              </span>
              <span className="bps-chart-header__description">
                {t("bpsMarket.proportionDescription")}
              </span>
              <span className="bps-chart-header__unit">
                {t("bpsMarket.proportionUnit")}
              </span>
            </div>
            <div className="bps-chart-header__branch">
              {t("lifeSelector.noLife")}
            </div>
          </div>
          <div className="bps-chart">
            <LineChart
              bpsRecords={bpsDataNoLife}
              chartWidth={400}
              chartHeight={400}
              selectedSeries={undefined}
              yDomain={yDomain}
            />
          </div>
        </div>
      </div>
    </Charts>
  );
};

export default BpsMarketComponent;
